import { z } from "zod";

/**
 * Dossiers candidats (devis, module « Dossiers candidats » et « Recherche et
 * retraçage de profils »). Les listes ci-dessous sont des DÉFAUTS : la
 * cartographie des jours 1–2 les précisera (statuts réels du parcours, niveaux
 * d'études pertinents, professions ciblées).
 */
export const CANDIDATE_STATUSES = [
  "nouveau",
  "en_evaluation",
  "dossier_en_cours",
  "soumis_avocat",
  "approuve",
  "refuse",
  "archive",
] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const CANDIDATE_STATUS_LABELS: Record<CandidateStatus, string> = {
  nouveau: "Nouveau",
  en_evaluation: "En évaluation",
  dossier_en_cours: "Dossier en cours",
  soumis_avocat: "Soumis à l'avocat",
  approuve: "Approuvé",
  refuse: "Refusé",
  archive: "Archivé",
};

export const SEXES = ["F", "M", "X"] as const;
export type Sex = (typeof SEXES)[number];
export const SEX_LABELS: Record<Sex, string> = { F: "Femme", M: "Homme", X: "Autre / non précisé" };

export const EDUCATION_LEVELS = [
  "aucun",
  "primaire",
  "secondaire",
  "professionnel",
  "collegial",
  "universitaire_1",
  "universitaire_2",
  "universitaire_3",
  "autre",
] as const;
export type EducationLevel = (typeof EDUCATION_LEVELS)[number];
export const EDUCATION_LABELS: Record<EducationLevel, string> = {
  aucun: "Aucun diplôme",
  primaire: "Primaire",
  secondaire: "Secondaire",
  professionnel: "Formation professionnelle",
  collegial: "Collégial / technique",
  universitaire_1: "Universitaire 1er cycle",
  universitaire_2: "Universitaire 2e cycle",
  universitaire_3: "Universitaire 3e cycle",
  autre: "Autre",
};

const optionalText = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === null || v === "" ? null : v));

export const candidateInputSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().min(1).max(120),
  sex: z.enum(SEXES).optional().nullable(),
  /** AAAA-MM-JJ, chiffré au repos ; seule l'année reste en clair pour les tranches d'âge. */
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "date AAAA-MM-JJ attendue")
    .optional()
    .nullable(),
  nationality: optionalText(80),
  countryResidence: optionalText(80),
  city: optionalText(120),
  email: z.union([z.string().trim().email(), z.literal("")]).optional().nullable(),
  phone: optionalText(40),
  profession: optionalText(160),
  educationLevel: z.enum(EDUCATION_LEVELS).optional().nullable(),
  educationDetail: optionalText(400),
  experienceYears: z.coerce.number().int().min(0).max(60).optional().nullable(),
  languages: z.array(z.string().trim().min(1).max(40)).max(12).optional(),
  status: z.enum(CANDIDATE_STATUSES).optional(),
  notes: z.string().max(10_000).optional().nullable(),
  /** Numéro de passeport, chiffré au repos. */
  passportNumber: optionalText(40),
  /** Expiration du passeport, AAAA-MM-JJ. */
  passportExpiresAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
});
export type CandidateInput = z.infer<typeof candidateInputSchema>;

export const candidateUpdateSchema = candidateInputSchema.partial();
export type CandidateUpdate = z.infer<typeof candidateUpdateSchema>;

export const candidateQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  sex: z.enum(SEXES).optional(),
  ageMin: z.coerce.number().int().min(0).max(120).optional(),
  ageMax: z.coerce.number().int().min(0).max(120).optional(),
  profession: z.string().trim().max(160).optional(),
  country: z.string().trim().max(80).optional(),
  educationLevel: z.enum(EDUCATION_LEVELS).optional(),
  status: z.enum(CANDIDATE_STATUSES).optional(),
  /** Recherche dans le contenu des CV (texte extrait des pièces). */
  cv: z.string().trim().max(200).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type CandidateQuery = z.infer<typeof candidateQuerySchema>;

export const assignLawyerSchema = z.object({ userId: z.string().min(1) });

export interface Candidate {
  id: string;
  reference: string;
  firstName: string;
  lastName: string;
  sex: Sex | null;
  birthYear: number | null;
  /** Présent uniquement sur la fiche détaillée (déchiffré à la demande, journalisé). */
  birthDate?: string | null;
  age: number | null;
  nationality: string | null;
  countryResidence: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  profession: string | null;
  educationLevel: EducationLevel | null;
  educationDetail: string | null;
  experienceYears: number | null;
  languages: string[];
  status: CandidateStatus;
  notes: string | null;
  passportNumber?: string | null;
  passportExpiresAt: string | null;
  createdBy: string | null;
  createdAt: number | string;
  updatedAt: number | string;
  assignedLawyers?: { userId: string; name: string; email: string }[];
}

export interface CandidateEvent {
  id: string;
  candidateId: string;
  at: number | string;
  userId: string | null;
  userName: string | null;
  type: string;
  details: Record<string, unknown> | null;
}

export interface CandidatePage {
  items: Candidate[];
  total: number;
  page: number;
  pageSize: number;
}
